import React, {useCallback, useEffect, useRef, useState} from 'react';
import {Alert, Autocomplete, CircularProgress, Stack, TextField} from '@mui/material';
import {useTranslation} from 'react-i18next';
import {useApplicationContext} from 'ApplicationContext';
import {GeocodeResultModel} from 'apiClient/Api';

interface LocationAutocompleteProps {
    /** Aktueller Text im Eingabefeld. */
    value: string;
    /** Der ausgewählte Treffer, oder null bei freiem Text. */
    selected: GeocodeResultModel | null;
    /**
     * Meldet jede Änderung. `selected` ist gesetzt, sobald ein Vorschlag gewählt
     * wurde — dann sollten dessen Koordinaten mitgespeichert werden, damit der
     * Server nicht erneut geocodieren muss.
     */
    onChange: (value: string, selected: GeocodeResultModel | null) => void;
    /** Beschriftung des Feldes. Default: „Adresse". */
    label?: string;
    /** Hilfetext unter dem Feld. */
    helperText?: string;
    /** Platzhalter im leeren Feld. */
    placeholder?: string;
    /** Sperrt die Eingabe. */
    disabled?: boolean;
    /** Größe des Eingabefeldes. */
    size?: 'small' | 'medium';
}

/** Verzögerung, bevor eine Eingabe an die Adress-Suche geschickt wird. */
const SEARCH_DEBOUNCE_MS = 400;

/** Ab dieser Eingabelänge sucht der Server (siehe GeocodingController). */
const MIN_SEARCH_LENGTH = 3;

/**
 * Adress-Eingabe mit Vorschlägen aus `/Geocoding/Search`. Freier Text ist
 * erlaubt — er wird dann serverseitig aufgelöst.
 *
 * Wird überall genutzt, wo ein Anlagen-Standort erfasst wird: beim Anlegen,
 * beim XML-Upload und auf der Detailseite.
 */
export const LocationAutocomplete: React.FC<LocationAutocompleteProps> = ({
    value,
    selected,
    onChange,
    label,
    helperText,
    placeholder,
    disabled,
    size,
}) => {
    const {t} = useTranslation();
    const context = useApplicationContext();

    const [options, setOptions] = useState<GeocodeResultModel[]>([]);
    const [searching, setSearching] = useState(false);
    const [searchError, setSearchError] = useState<string | null>(null);

    const searchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => () => {
        if (searchTimer.current) {
            clearTimeout(searchTimer.current);
        }
    }, []);

    const runSearch = useCallback(async (query: string) => {
        setSearching(true);
        setSearchError(null);
        try {
            const response = await context.api.geocoding.geocodingSearch({q: query, limit: 5});
            setOptions(response.data ?? []);
        } catch (err: any) {
            setOptions([]);
            setSearchError(
                err?.response?.status === 503
                    ? t('deviceManagement.licensePage.location.errors.geocoderUnavailable')
                    : t('deviceManagement.licensePage.location.errors.searchFailed'),
            );
        } finally {
            setSearching(false);
        }
    }, [context.api.geocoding, t]);

    const handleInputChange = (next: string): void => {
        // Sobald wieder getippt wird, gilt der frühere Treffer nicht mehr.
        onChange(next, null);

        if (searchTimer.current) {
            clearTimeout(searchTimer.current);
        }

        const query = next.trim();
        if (query.length < MIN_SEARCH_LENGTH) {
            setOptions([]);
            setSearchError(null);
            return;
        }

        searchTimer.current = setTimeout(() => runSearch(query), SEARCH_DEBOUNCE_MS);
    };

    return (
        <Stack spacing={1}>
            <Autocomplete
                freeSolo
                filterOptions={(x) => x}
                options={options}
                loading={searching}
                inputValue={value}
                value={selected}
                disabled={disabled}
                size={size}
                noOptionsText={t('deviceManagement.licensePage.location.noResults')}
                getOptionLabel={(option) => (typeof option === 'string' ? option : option.displayName ?? '')}
                isOptionEqualToValue={(option, candidate) =>
                    typeof candidate === 'string'
                        ? option.displayName === candidate
                        : option.displayName === candidate.displayName
                        && option.latitude === candidate.latitude
                        && option.longitude === candidate.longitude
                }
                onInputChange={(_event, next, reason) => {
                    if (reason === 'input') {
                        handleInputChange(next);
                    } else if (reason === 'clear') {
                        handleInputChange('');
                    }
                }}
                onChange={(_event, next) => {
                    if (next && typeof next !== 'string') {
                        onChange(next.displayName ?? '', next);
                    } else {
                        onChange(typeof next === 'string' ? next : '', null);
                    }
                }}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        label={label ?? t('deviceManagement.licensePage.location.addressLabel')}
                        placeholder={placeholder ?? t('deviceManagement.licensePage.location.addressPlaceholder')}
                        helperText={helperText}
                        slotProps={{
                            ...params.slotProps,
                            input: {
                                ...params.slotProps.input,
                                endAdornment: (
                                    <>
                                        {searching ? <CircularProgress color="inherit" size={16}/> : null}
                                        {params.slotProps.input.endAdornment}
                                    </>
                                ),
                            },
                        }}
                    />
                )}
            />
            {searchError && <Alert severity="warning">{searchError}</Alert>}
        </Stack>
    );
};

export default LocationAutocomplete;
